import Select, { components } from "react-select";
import { isMobile } from "../common";
import {
  useMemo,
  useRef,
  useLayoutEffect,
  useEffect,
  useState,
  useCallback,
} from "react";
import { normalizeDisplayLabel } from "../utility/displayText";

function AutoPositionMenu(props: any) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuShift, setMenuShift] = useState({ x: 0, y: 0 });

  // Track the applied shift in a ref so re-measures can subtract it and read the
  // menu's true (un-translated) anchor rect — otherwise each pass compounds.
  const shiftRef = useRef({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const viewportPadding = 8;

    const updateMenuPosition = () => {
      const wrapper = menuRef.current;
      if (!wrapper) return;
      // The wrapper is a zero-size inline box; the real menu is its absolutely-
      // positioned child. Measure the child, transform the wrapper.
      const el = (wrapper.firstElementChild as HTMLElement) ?? wrapper;

      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      // Un-apply the current transform to get the natural anchored position.
      const baseLeft = rect.left - shiftRef.current.x;
      const baseRight = rect.right - shiftRef.current.x;
      const baseTop = rect.top - shiftRef.current.y;
      const baseBottom = rect.bottom - shiftRef.current.y;
      const width = baseRight - baseLeft;

      let nextX = 0;
      let nextY = 0;

      // Overflow on the right → slide left so it opens toward the left.
      const rightOverflow = baseRight - (window.innerWidth - viewportPadding);
      if (rightOverflow > 0) {
        nextX -= rightOverflow;
      }

      // After the left slide, if it now clips the left edge, pin to padding.
      // (Only possible when the menu is wider than the viewport.)
      const leftOverflow = viewportPadding - (baseLeft + nextX);
      if (leftOverflow > 0) {
        nextX += leftOverflow;
      }

      // Never push the right edge back off-screen when pinned left.
      if (baseLeft + nextX + width > window.innerWidth - viewportPadding) {
        nextX = window.innerWidth - viewportPadding - width - baseLeft;
      }

      const bottomOverflow =
        baseBottom - (window.innerHeight - viewportPadding);
      if (bottomOverflow > 0) {
        nextY -= bottomOverflow;
      }

      const topOverflow = viewportPadding - (baseTop + nextY);
      if (topOverflow > 0) {
        nextY += topOverflow;
      }

      shiftRef.current = { x: nextX, y: nextY };
      setMenuShift((previous) => {
        if (previous.x === nextX && previous.y === nextY) {
          return previous;
        }
        return { x: nextX, y: nextY };
      });
    };

    updateMenuPosition();
    // The menu child may mount a frame later; re-measure on the next frame.
    const raf = requestAnimationFrame(updateMenuPosition);

    // Re-measure when the menu resizes (async option load, search filtering)
    // so a menu that grows wider after mount still reflows away from the edge.
    let resizeObserver: ResizeObserver | undefined;
    const observeTarget =
      (menuRef.current?.firstElementChild as HTMLElement) ?? menuRef.current;
    if (typeof ResizeObserver !== "undefined" && observeTarget) {
      resizeObserver = new ResizeObserver(() => updateMenuPosition());
      resizeObserver.observe(observeTarget);
    }
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, []);

  const transform =
    menuShift.x !== 0 || menuShift.y !== 0
      ? `translate(${menuShift.x}px, ${menuShift.y}px)`
      : undefined;

  return (
    <div
      ref={menuRef}
      style={{ transform }}
      data-search-instance={props.selectProps?.searchInstanceId}
    >
      <components.Menu {...props} />
    </div>
  );
}
type Option = {
  value: string | number;
  label: string;
  isFixed?: boolean;
  isDisabled?: boolean;
};

type SharedSearchProps = {
  options: Option[];
  selectedOptions: Option[];
  placeholder: string;
  totalCount?: number | null;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  menuMaxHeight?: number;
  isLoading?: boolean;
  menuPortalTarget?: HTMLElement | null;
  isClearable?: boolean;
  isDisabled?: boolean;
  noOptionsText?: string;
  loadingText?: string;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  menuPlacement?: "auto" | "top" | "bottom";
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  clearAllLabel?: string;
  maxVisibleTags?: number;
  className?: string;
  isOptionDisabled?: (option: Option) => boolean;
  sortAlphabetically?: boolean;
  autoWidth?: boolean;
  /**
   * Optional field label rendered as a small uppercase caption above the
   * selected value inside the control (floating-label style). When set, the
   * trigger switches to a two-line layout: LABEL on top, value below.
   */
  label?: string;
  /** Accent colour for the floating label, focus ring and checkboxes. */
  accentColor?: string;
  /**
   * For labelled (two-line) triggers: when nothing is selected, show only the
   * `label` as a single centred line instead of the caption + placeholder
   * stack. Once a value is picked the normal caption-over-value layout returns.
   */
  collapseLabelWhenEmpty?: boolean;
  /**
   * Force radio (single-pick) menu rows even when `isMany` is true. Use for
   * hierarchy boxes that are multi under the hood (last-pick-wins) but should
   * read as single-select to the user.
   */
  singleSelect?: boolean;
  /**
   * Imperative open trigger. Each time this number increases (and is > 0) the
   * grouped menu opens and its search input gains focus. Used by the discovery
   * "Try it" tip to surface the feeder picker. Ignored when unset.
   */
  openSignal?: number;
};

type MultiSearchProps = SharedSearchProps & {
  isMany: true;
  onChange: (selected: Option[], actionMeta?: any) => void;
};

type SingleSearchProps = SharedSearchProps & {
  isMany?: false;
  onChange: (selected: Option | null, actionMeta?: any) => void;
};

type SearchBoxProps = MultiSearchProps | SingleSearchProps;

let searchBoxInstanceCounter = 0;

function normalizeOptionLabel(option: Option): Option {
  return {
    ...option,
    label: normalizeDisplayLabel(option.label),
  };
}

/**
 * Single shared menu-option row used by every SearchBox menu. Shows a square
 * checkbox for multi-select and a round radio for single-select.
 */
export function SelectMenuOption(props: {
  label: string;
  checked: boolean;
  variant: "checkbox" | "radio";
  showControl: boolean;
  accentColor: string;
}) {
  const { label, checked, variant, showControl, accentColor } = props;
  const isRadio = variant === "radio";
  return (
    <span
      title={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        whiteSpace: "nowrap",
        lineHeight: 1.25,
      }}
    >
      {showControl && (
        <span
          aria-hidden
          style={{
            flexShrink: 0,
            width: 16,
            height: 16,
            borderRadius: isRadio ? "50%" : 4,
            border: `1.5px solid ${checked ? accentColor : "#D1D5DB"}`,
            backgroundColor: checked && !isRadio ? accentColor : "#FFFFFF",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 120ms ease",
          }}
        >
          {checked &&
            (isRadio ? (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: accentColor,
                }}
              />
            ) : (
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2.5 6.5L5 9l4.5-5" />
              </svg>
            ))}
        </span>
      )}
      <span
        style={{
          flex: "1 1 auto",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </span>
  );
}

export const searchStyle = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: isMobile ? 32 : 37,
    borderRadius: 10,
    overflow: "hidden",
    borderColor: state.isDisabled
      ? "#E5E7EB"
      : state.isFocused
        ? "#F1935C"
        : "#D1D5DB",
    boxShadow: state.isDisabled
      ? "none"
      : state.isFocused
        ? "0 0 0 3px rgba(148,163,184,0.2)"
        : "0 1px 2px rgba(15,23,42,0.04)",
    backgroundColor: state.isDisabled ? "#F3F4F6" : "#FFFFFF",
    "&:hover": {
      borderColor: state.isDisabled
        ? "#E5E7EB"
        : state.isFocused
          ? "#F1935C"
          : "#9CA3AF",
    },
    cursor: state.isDisabled ? "not-allowed" : "default",
    transition: "all 120ms ease",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#6B7280",
    fontSize: isMobile ? 10 : 12,
  }),
  valueContainer: (base: any) => ({
    ...base,
    maxHeight: isMobile ? 32 : 37,
    minHeight: isMobile ? 32 : 37,
    fontSize: 9,
    overflowX: "hidden",
    overflowY: "auto",
    flexWrap: "wrap" as const,
    scrollbarWidth: "thin" as const,
    flex: "1 1 0",
    minWidth: 0,
  }),
  input: (base: any) => ({
    ...base,
    minWidth: "56px",
    flex: "1 1 auto",
  }),
  singleValue: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "#9CA3AF" : "#111827",
  }),
  multiValue: (base: any, state: { data: { isFixed: any } }) => {
    return state.data.isFixed
      ? {
          ...base,
          backgroundColor: "#C26B35",
          flexShrink: 1,
          minWidth: 0,
          maxWidth: "100%",
        }
      : {
          ...base,
          backgroundColor: "#FDE7D9",
          borderRadius: 6,
          flexShrink: 1,
          minWidth: 0,
          maxWidth: "100%",
        };
  },
  multiValueLabel: (base: any, state: { data: { isFixed: any } }) => {
    return state.data.isFixed
      ? {
          ...base,
          fontWeight: "bold",
          color: "white",
          paddingRight: 6,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }
      : {
          ...base,
          color: "#A44E1E",
          fontWeight: 500,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        };
  },
  menuList: (base: any) => ({
    ...base,
    maxHeight: 240,
    paddingTop: 4,
    paddingBottom: 4,
    overflowX: "hidden",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 9,
    backgroundColor: state.isDisabled
      ? "#F9FAFB"
      : state.isSelected
        ? "#FEF0E7"
        : state.isFocused
          ? "#FFF8F4"
          : "white",
    color: state.isDisabled ? "#9CA3AF" : "#111827",
    padding: "8px 10px",
    whiteSpace: "nowrap" as const,
    lineHeight: 1.25,
    display: "block",
    width: "100%",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
  multiValueRemove: (
    base: any,
    state: {
      data: {
        value: any;
        isFixed: any;
      };
    },
  ) => {
    return state.data.isFixed ? { ...base, display: "none" } : base;
  },
  menu: (base: any) => ({
    ...base,
    width: "100%",
    minWidth: "100%",
    maxWidth: isMobile ? "92vw" : 420,
    marginTop: 6,
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
    zIndex: 9999,
    overflow: "hidden",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled
      ? "#D1D5DB"
      : state.isFocused
        ? "#F1935C"
        : "#D1D5DB",
    padding: 6,
    transition: "all 120ms ease",
  }),
  clearIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "#D1D5DB" : "#B38164",
    padding: 6,
    ":hover": {
      color: state.isDisabled ? "#D1D5DB" : "#D97C44",
    },
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    flexShrink: 0,
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    marginTop: 6,
    marginBottom: 6,
    backgroundColor: "#F7D7C2",
  }),
  noOptionsMessage: (base: any) => ({
    ...base,
    color: "#6B7280",
    fontSize: isMobile ? 11 : 12,
  }),
  loadingMessage: (base: any) => ({
    ...base,
    color: "#6B7280",
    fontSize: isMobile ? 11 : 12,
  }),
};

export function SearchBox(props: SearchBoxProps) {
  const {
    options,
    selectedOptions,
    onChange,
    placeholder,
    totalCount,
    width,
    minWidth,
    maxWidth,
    menuMaxHeight,
    isLoading,
    menuPortalTarget,
    isClearable = false,
    isDisabled = false,
    noOptionsText,
    loadingText,
    closeMenuOnSelect,
    hideSelectedOptions = false,
    menuPlacement = "auto",
    enableSelectAll = false,
    selectAllLabel = "Select all",
    clearAllLabel = "Clear",
    className,
    isOptionDisabled,
    sortAlphabetically = false,
    autoWidth = false,
    label,
    accentColor = "#F1935C",
    collapseLabelWhenEmpty = false,
    singleSelect = false,
    openSignal,
  } = props;
  const isMany = props.isMany === true;
  const hasLabel = typeof label === "string" && label.length > 0;

  // The grouped menu (Selected/Not-selected) renders its own search field. To
  // let the user type without react-select stealing/closing on blur, we hide
  // the native input and control the menu's open state ourselves.
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  // Unique tag so the outside-click handler can tell this instance's control +
  // (portalled) menu apart from sibling SearchBoxes in the same row.
  const instanceIdRef = useRef<string | null>(null);
  if (instanceIdRef.current == null) {
    searchBoxInstanceCounter += 1;
    instanceIdRef.current = `sb-${searchBoxInstanceCounter}`;
  }
  const instanceId = instanceIdRef.current;

  const safeOptions = useMemo(() => options ?? [], [options]);
  const safeSelectedOptions = useMemo(
    () => selectedOptions ?? [],
    [selectedOptions],
  );
  const normalizedOptions = useMemo(() => {
    const mapped = safeOptions.map((option) => normalizeOptionLabel(option));
    return sortAlphabetically
      ? mapped.sort((a, b) => a.label.localeCompare(b.label))
      : mapped;
  }, [safeOptions, sortAlphabetically]);
  const normalizedSelectedOptions = useMemo(
    () => safeSelectedOptions.map((option) => normalizeOptionLabel(option)),
    [safeSelectedOptions],
  );
  const resolvedMinWidth = minWidth ?? (isMobile ? 140 : 180);
  const resolvedMaxWidth = maxWidth ?? (isMobile ? "72vw" : 360);
  const autoComputedWidth = useMemo(() => {
    if (!autoWidth || safeSelectedOptions.length === 0) return null;
    const totalChars = safeSelectedOptions.reduce(
      (sum, o) => sum + (o.label?.length ?? 0),
      0,
    );
    // ~6px per char at 9px font + 36px per tag (padding + remove btn) + 52px for indicators
    const computed = totalChars * 6 + safeSelectedOptions.length * 36 + 52;
    const min = typeof resolvedMinWidth === "number" ? resolvedMinWidth : 120;
    const max = typeof resolvedMaxWidth === "number" ? resolvedMaxWidth : 480;
    return Math.min(Math.max(computed, min), max);
  }, [autoWidth, safeSelectedOptions, resolvedMinWidth, resolvedMaxWidth]);
  const resolvedMenuPortalTarget = useMemo(() => {
    if (menuPortalTarget !== undefined) {
      return menuPortalTarget ?? undefined;
    }
    if (typeof document !== "undefined") {
      return document.body;
    }
    return undefined;
  }, [menuPortalTarget]);

  const value = isMany
    ? normalizedSelectedOptions.map((option) => ({
        ...option,
        isFixed: option.isFixed ?? false,
      }))
    : normalizedSelectedOptions[0]
      ? {
          ...normalizedSelectedOptions[0],
          isFixed: normalizedSelectedOptions[0].isFixed ?? false,
        }
      : null;

  const resolvedIsOptionDisabled = useCallback(
    (option: Option) => {
      return (
        option.isDisabled === true ||
        (isOptionDisabled != null && isOptionDisabled(option))
      );
    },
    [isOptionDisabled],
  );

  const selectableOptions = useMemo(
    () =>
      normalizedOptions.filter((option) => !resolvedIsOptionDisabled(option)),
    [normalizedOptions, resolvedIsOptionDisabled],
  );

  // Show the sticky header whenever we have bulk controls OR a field label to
  // surface (image-style "LABEL ... N selected" header).
  // Single-select boxes still get the rich menu (sticky search + grouping) when
  // a field label is set; select-all is multi-only and stays gated on isMany.
  const menuActionsEnabled = (isMany && enableSelectAll) || hasLabel;

  // Close the controlled menu on outside mousedown (react-select's native
  // outside-close is bypassed since the native input is hidden in grouped mode).
  // Without this, opening one trigger leaves sibling menus open too.
  useEffect(() => {
    if (!menuActionsEnabled || !menuIsOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest(`[data-search-instance="${instanceId}"]`)) {
        return;
      }
      setMenuIsOpen(false);
      setInputValue("");
    };
    document.addEventListener("mousedown", handleOutside, true);
    return () => document.removeEventListener("mousedown", handleOutside, true);
  }, [menuActionsEnabled, menuIsOpen, instanceId]);

  // Preserve the menu scroll position across selections. Splitting rows into
  // Selected / Not-selected groups reorders the DOM when an item is picked,
  // which would otherwise snap the scroll back to the top.
  const menuScrollRef = useRef<HTMLDivElement | null>(null);
  const savedScrollTopRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Imperative open: when `openSignal` increments (> 0) open the grouped menu and
  // focus its search field. Skips the initial mount value so it only fires on a
  // genuine bump from the parent (e.g. discovery "Try it").
  const prevOpenSignalRef = useRef<number | undefined>(openSignal);
  useEffect(() => {
    if (openSignal == null || openSignal <= 0) return;
    if (openSignal === prevOpenSignalRef.current) return;
    prevOpenSignalRef.current = openSignal;
    setMenuIsOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [openSignal]);

  const MenuListWithActions = useMemo(() => {
    if (!menuActionsEnabled) {
      return undefined;
    }

    return (props: any) => {
      // Split the rendered option rows into selected vs not-selected groups.
      const childArray = Array.isArray(props.children)
        ? props.children
        : [props.children];
      // Read the live selection from selectProps so this component identity
      // stays stable across selections (recreating it would reset scroll).
      const liveSelected = (props.getValue?.() ?? []) as Option[];
      const liveSelectedSet = new Set(liveSelected.map((o) => String(o.value)));
      const selectedChildren: any[] = [];
      const unselectedChildren: any[] = [];
      let hasOptionRows = false;
      childArray.forEach((child: any) => {
        const data = child?.props?.data;
        if (data == null || data.value === undefined) {
          // Non-option node (e.g. NoOptionsMessage/LoadingMessage) — keep as-is.
          unselectedChildren.push(child);
          return;
        }
        hasOptionRows = true;
        if (liveSelectedSet.has(String(data.value))) {
          selectedChildren.push(child);
        } else {
          unselectedChildren.push(child);
        }
      });

      const groupHeading = (text: string) => (
        <div
          style={{
            padding: "8px 10px 4px",
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#B6BAC3",
          }}
        >
          {text}
        </div>
      );

      // react-select passes innerRef to the scrollable element. Wrap it so we
      // can save the scroll position and restore it after the group reorder.
      const attachScroll = (el: HTMLDivElement | null) => {
        menuScrollRef.current = el;
        if (typeof props.innerRef === "function") {
          props.innerRef(el);
        }
        if (el) {
          // Restore synchronously on the post-select commit.
          el.scrollTop = savedScrollTopRef.current;
        }
      };

      return (
        <components.MenuList
          {...props}
          innerRef={attachScroll}
          innerProps={{
            ...props.innerProps,
            onScroll: (e: any) => {
              savedScrollTopRef.current = e.currentTarget.scrollTop;
              props.innerProps?.onScroll?.(e);
            },
          }}
        >
          {/* Minimal sticky header: search field + optional bulk actions */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "#FFFFFF",
              padding: "6px 6px 4px",
              borderBottom: "1px solid #F1F2F4",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                background: "#F4F5F7",
                borderRadius: 6,
                padding: "0 7px",
              }}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={props.selectProps?.menuSearch ?? ""}
                placeholder="Search..."
                autoComplete="off"
                autoFocus
                onChange={(e) =>
                  props.selectProps?.onMenuSearchChange?.(e.target.value)
                }
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Escape") {
                    props.selectProps?.onMenuClose?.();
                  }
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "none",
                  background: "transparent",
                  padding: "5px 0",
                  fontSize: 7.5,
                  color: "#374151",
                  outline: "none",
                }}
              />
            </div>
            {enableSelectAll && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "6px 2px 0",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    (onChange as (selected: Option[]) => void)(
                      selectableOptions,
                    );
                  }}
                  style={{
                    border: "none",
                    background: "none",
                    color: accentColor,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {selectAllLabel}
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    (onChange as (selected: Option[]) => void)([]);
                  }}
                  style={{
                    border: "none",
                    background: "none",
                    color: "#9CA3AF",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {clearAllLabel}
                </button>
              </div>
            )}
          </div>
          {hasOptionRows ? (
            <>
              {selectedChildren.length > 0 && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 10px 4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#B6BAC3",
                      }}
                    >
                      Selected ({selectedChildren.length})
                    </span>
                    <button
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (isMany) {
                          (onChange as (selected: Option[]) => void)([]);
                        } else {
                          (onChange as (selected: Option | null) => void)(null);
                        }
                      }}
                      style={{
                        border: "none",
                        background: "none",
                        color: accentColor,
                        fontSize: 9,
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      {clearAllLabel}
                    </button>
                  </div>
                  {selectedChildren}
                </>
              )}
              {unselectedChildren.length > 0 && (
                <>
                  {groupHeading("Not selected")}
                  {unselectedChildren}
                </>
              )}
            </>
          ) : (
            unselectedChildren
          )}
        </components.MenuList>
      );
    };
  }, [
    menuActionsEnabled,
    onChange,
    selectableOptions,
    selectAllLabel,
    clearAllLabel,
    enableSelectAll,
    accentColor,
    isMany,
  ]);

  const ClearAllIndicator = useMemo(() => {
    return (props: any) => {
      return (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            props.clearValue();
          }}
          style={{
            flexShrink: 0,
            border: "none",
            background: "none",
            padding: "0 4px",
            cursor: "pointer",
            color: "#9CA3AF",
            fontSize: 11,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLElement).style.color = "#EF4444")
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLElement).style.color = "#9CA3AF")
          }
        >
          ✕
        </button>
      );
    };
  }, []);

  const TooltipControl = useMemo(() => {
    return (props: any) => {
      const selected = props.getValue() as Option[];
      const title =
        selected.length > 0
          ? selected
              .map((o) => normalizeDisplayLabel(o.label).toUpperCase())
              .join(", ")
          : undefined;
      // In grouped mode the native input is hidden, so react-select won't open
      // on focus — toggle the controlled menu from the trigger's mousedown.
      const toggleMenu = props.selectProps?.onMenuToggle;
      const controlInnerProps = toggleMenu
        ? {
            ...props.innerProps,
            title,
            "data-search-instance": props.selectProps?.searchInstanceId,
            onMouseDown: (e: any) => {
              e.preventDefault();
              toggleMenu();
              props.innerProps?.onMouseDown?.(e);
            },
          }
        : { ...props.innerProps, title };
      return (
        <components.Control {...props} innerProps={controlInnerProps}>
          {totalCount != null && (
            <div className="flex flex-col items-center justify-center bg-[#f1f3f6] border-r m-[3px] border-[#E5E7EB] rounded-[9px] p-1 px-2 self-stretch flex-shrink-0 select-none leading-none">
              <span className="text-[10px] font-medium text-[#3D4354] leading-tight">
                {totalCount}
              </span>
              <span className="text-[6px] font-medium text-[#6F6D7C] uppercase tracking-wider leading-tight mt-0.5">
                Total
              </span>
            </div>
          )}
          {props.children}
        </components.Control>
      );
    };
  }, [totalCount]);

  // Floating uppercase caption above the value (image-style two-line trigger).
  const LabelledValueContainer = useMemo(() => {
    if (!hasLabel) return undefined;
    return (props: any) => {
      const selected = (props.getValue?.() ?? []) as Option[];
      const first =
        selected.length > 0
          ? normalizeDisplayLabel(selected[0].label).toUpperCase()
          : null;
      const overflow = selected.length > 1 ? selected.length - 1 : 0;
      const allLabels = selected
        .map((o) => normalizeDisplayLabel(o.label).toUpperCase())
        .join(", ");
      // While the user is typing a search query, step out of the overlay so the
      // native input text is visible.
      const isTyping = Boolean(props.selectProps?.inputValue);
      // Collapsed-empty mode: nothing selected + caller opted in → render the
      // label alone as a single centred line (no caption/placeholder stack).
      const showCollapsedLabel =
        collapseLabelWhenEmpty && first == null && !isTyping;

      return (
        <components.ValueContainer {...props}>
          {/* Single stable wrapper for react-select's native input/value — never
              remount it (that would drop focus after one keystroke). When typing
              it fills the control and shows the text; when idle it collapses to 0
              so the two-line overlay shows instead. */}
          <span
            style={
              isTyping
                ? {
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    paddingLeft: 10,
                    paddingRight: 4,
                  }
                : {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                    opacity: 0,
                  }
            }
          >
            {props.children}
          </span>
          {/* Two-line overlay — fills the full control box. Hidden while typing. */}
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              display: isTyping ? "none" : "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingLeft: 12,
              paddingRight: 4,
              pointerEvents: "none",
              gap: 2,
            }}
          >
            {/* Caption — hidden in collapsed-empty mode so only the single label shows */}
            {!showCollapsedLabel && (
              <span
                style={{
                  fontSize: 7.5,
                  fontWeight: 500,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#9CA3AF",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {label}
              </span>
            )}
            {/* Value row */}
            {showCollapsedLabel ? (
              <span
                style={{
                  color: "#6B7280",
                  fontWeight: 500,
                  fontSize: 12,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {label}
              </span>
            ) : first != null ? (
              <div
                title={allLabels}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    color: "#34676e",
                    fontWeight: 600,
                    fontSize: 9.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1,
                    flexShrink: 1,
                    minWidth: 0,
                  }}
                >
                  {first}
                </span>
                {overflow > 0 && (
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#F1935C",
                      backgroundColor: "#FEF0E7",
                      borderRadius: 10,
                      padding: "1px 6px",
                      lineHeight: 1.4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    +{overflow}
                  </span>
                )}
              </div>
            ) : (
              <span
                style={{
                  color: "#9CA3AF",
                  fontWeight: 400,
                  fontSize: 11,
                  lineHeight: 1.2,
                }}
              >
                {props.selectProps?.placeholder ?? ""}
              </span>
            )}
          </div>
        </components.ValueContainer>
      );
    };
  }, [hasLabel, label, collapseLabelWhenEmpty]);

  // singleSelect forces radio even on an isMany box (last-pick-wins hierarchy).
  const showRadio = singleSelect || !isMany;
  const CustomOption = useMemo(() => {
    return (optionProps: any) => {
      const optionLabel = normalizeDisplayLabel(
        optionProps.label,
      ).toUpperCase();
      // Multi-select → checkbox, single-select → radio. Control only shown for
      // multi or labelled single-select rows.
      return (
        <components.Option {...optionProps}>
          <SelectMenuOption
            label={optionLabel}
            checked={optionProps.isSelected}
            variant={showRadio ? "radio" : "checkbox"}
            showControl={isMany || hasLabel}
            accentColor={accentColor}
          />
        </components.Option>
      );
    };
  }, [isMany, showRadio, accentColor, hasLabel]);

  const CustomSingleValue = useMemo(() => {
    return (props: any) => {
      const label = normalizeDisplayLabel(props.data.label).toUpperCase();
      const isFixed = props.data.isFixed;
      // With a floating field label, the value renders as plain bold dark text
      // (no pill) to match the two-line trigger design.
      if (hasLabel) {
        return (
          <components.SingleValue {...props}>
            <span
              title={label}
              style={{
                display: "block",
                color: "#1F2937",
                fontWeight: 700,
                fontSize: 13,
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.2,
              }}
            >
              {label}
            </span>
          </components.SingleValue>
        );
      }
      return (
        <components.SingleValue {...props}>
          <span
            title={label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: isFixed ? "#C26B35" : "#FDE7D9",
              color: isFixed ? "white" : "#A44E1E",
              fontWeight: isFixed ? "bold" : 500,
              borderRadius: 6,
              padding: "2px 8px",
              fontSize: 9,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.4,
            }}
          >
            {label}
          </span>
        </components.SingleValue>
      );
    };
  }, [hasLabel]);

  const CustomMultiValue = useMemo(() => {
    return (props: any) => {
      // In labelled mode the selected values are summarised once by the
      // ValueContainer (bold value or "N selected"); hide the individual chips.
      if (hasLabel) return null;
      return (
        <components.MultiValue {...props}>
          {normalizeDisplayLabel(props.data.label).toUpperCase()}
        </components.MultiValue>
      );
    };
  }, [hasLabel]);

  const customComponents = useMemo(() => {
    const nextComponents: any = {
      Option: CustomOption,
      SingleValue: CustomSingleValue,
      MultiValue: CustomMultiValue,
      Menu: AutoPositionMenu,
    };

    if (MenuListWithActions != null) {
      nextComponents.MenuList = MenuListWithActions;
      // Hide react-select's native input — the menu renders its own search
      // field and we drive open/filter state manually.
      nextComponents.Input = () => null;
    }

    if (ClearAllIndicator != null) {
      nextComponents.ClearIndicator = ClearAllIndicator;
    }

    if (TooltipControl != null) {
      nextComponents.Control = TooltipControl;
    }

    if (LabelledValueContainer != null) {
      nextComponents.ValueContainer = LabelledValueContainer;
      nextComponents.IndicatorSeparator = () => null;
      nextComponents.DropdownIndicator = (p: any) => (
        <div
          style={{ padding: "0 6px", display: "flex", alignItems: "center" }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            style={{
              color: p.isFocused ? accentColor : "#9CA3AF",
              transition: "color 120ms ease",
            }}
          >
            <path
              d="M2 3.5L5 6.5L8 3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }

    return nextComponents;
  }, [
    MenuListWithActions,
    ClearAllIndicator,
    accentColor,
    TooltipControl,
    LabelledValueContainer,
    CustomOption,
    CustomSingleValue,
    CustomMultiValue,
  ]);

  const styles = useMemo(() => {
    const base: any = { ...searchStyle };
    // Labelled (two-line) triggers default to a fixed width so a row of them
    // (e.g. CIRCLE › TRANSCO › DISCOM) stays uniform regardless of content.
    const resolvedWidth = width ?? (hasLabel ? 160 : undefined);
    const effectiveWidth = resolvedWidth ?? autoComputedWidth ?? undefined;
    const effectiveMinWidth =
      resolvedWidth != null ? resolvedWidth : resolvedMinWidth;
    const effectiveMaxWidth =
      resolvedWidth != null ? resolvedWidth : resolvedMaxWidth;
    // Two-line trigger is taller to fit the floating caption.
    const controlHeight = hasLabel ? (isMobile ? 33 : 30) : 37;
    base.container = (b: any) => ({
      ...b,
      minWidth: effectiveMinWidth,
      maxWidth: effectiveMaxWidth,
      width: effectiveWidth,
      height: controlHeight,
    });
    base.control = (b: any, state: any) => ({
      ...searchStyle.control(b, state),
      minWidth: effectiveMinWidth,
      maxWidth: effectiveMaxWidth,
      width: effectiveWidth,
      height: controlHeight,
      borderColor: state.isDisabled
        ? "#E5E7EB"
        : state.isFocused
          ? accentColor
          : "#D1D5DB",
      boxShadow: state.isFocused
        ? `0 0 0 3px ${accentColor}33`
        : searchStyle.control(b, state).boxShadow,
      "&:hover": {
        borderColor: state.isDisabled
          ? "#E5E7EB"
          : state.isFocused
            ? accentColor
            : "#9CA3AF",
      },
    });
    base.valueContainer = (b: any) => ({
      ...searchStyle.valueContainer(b),
      maxHeight: hasLabel ? controlHeight - 6 : isMobile ? 32 : 35,
      minHeight: hasLabel ? controlHeight - 6 : isMobile ? 32 : 35,
      height: hasLabel ? controlHeight - 6 : undefined,
      alignSelf: hasLabel ? "center" : undefined,
      overflowX: "hidden",
      overflowY: hasLabel ? "hidden" : "auto",
      flexWrap: hasLabel ? ("nowrap" as const) : "wrap",
      flex: "1 1 0",
      minWidth: 0,
      // Overlay handles the two-line layout; just need relative positioning.
      ...(hasLabel
        ? {
            position: "relative" as const,
            // Left padding aligns the typed-input text with the overlay caption.
            padding: "0 4px 0 10px",
            alignItems: "center",
          }
        : {}),
    });
    if (hasLabel) {
      // Clean two-line trigger: chevron only, no separator bar.
      base.indicatorSeparator = () => ({ display: "none" });
      base.dropdownIndicator = (b: any, state: any) => ({
        ...b,
        color: state.isFocused ? accentColor : "#9CA3AF",
        padding: 6,
      });
    }
    base.menu = (b: any) => ({
      ...searchStyle.menu(b),
      // Let the menu grow past the (fixed-width) trigger so long option labels
      // fit on one line instead of wrapping and forcing extra scroll.
      width: "max-content",
      minWidth: "100%",
      maxWidth: isMobile ? "92vw" : 420,
      zIndex: 9999,
      overflow: "visible",
    });

    base.menuList = (b: any) => ({
      ...b,
      ...(menuMaxHeight != null ? { maxHeight: menuMaxHeight } : {}),
      paddingTop: 0,
      paddingBottom: 0,
      overflow: "auto",
      borderRadius: "inherit",
    });
    base.menuPortal = (b: any) => ({ ...b, zIndex: 999999 });
    base.option = (b: any, state: any) => ({
      ...searchStyle.option(b, state),
      backgroundColor: state.isDisabled
        ? "#F9FAFB"
        : state.isSelected
          ? `${accentColor}18`
          : state.isFocused
            ? `${accentColor}0d`
            : "white",
    });

    return base;
  }, [
    width,
    autoComputedWidth,
    menuMaxHeight,
    resolvedMaxWidth,
    resolvedMinWidth,
    hasLabel,
    accentColor,
  ]);

  const handleChange = (nextValue: any, actionMeta?: any) => {
    if (isMany) {
      (onChange as (selected: Option[], actionMeta?: any) => void)(
        (nextValue ?? []) as Option[],
        actionMeta,
      );
      return;
    }
    (onChange as (selected: Option | null, actionMeta?: any) => void)(
      (nextValue ?? null) as Option | null,
      actionMeta,
    );
  };

  const filterOption = (candidate: any, rawInput: string) => {
    // In grouped mode the native input is hidden, so react-select's own
    // inputValue stays empty — filter against our controlled search state.
    const source = menuActionsEnabled ? inputValue : rawInput;
    const query = source.trim().toLowerCase();
    if (!query) {
      return true;
    }
    const cleanLabel = normalizeDisplayLabel(candidate.label).toLowerCase();
    const normalizedQuery = normalizeDisplayLabel(query);
    // Canonical form: lowercase + drop EVERYTHING that isn't a letter or digit
    // (spaces, hyphens, dots, slashes, brackets, &, etc.) so "may 25", "May-25",
    // "MAY.25" and "may/25" all match an option labelled "MAY-25". Diacritics are
    // folded too ("Ünït" → "unit") for accent-insensitive matching.
    const canon = (s: string) =>
      s
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]/g, "");
    const qCanon = canon(query);
    // Numeric `value`s are usually synthetic surrogate ids (e.g. a month index),
    // not user-facing text — matching against them lets an unrelated id digit
    // (e.g. "56") wrongly surface an option whose label never contained it.
    // Only search `value` when it's a real string code someone might type.
    const valueIsSearchableText = typeof candidate.value === "string";
    return (
      cleanLabel.includes(query) ||
      cleanLabel.includes(normalizedQuery) ||
      candidate.label.toLowerCase().includes(query) ||
      (valueIsSearchableText &&
        String(candidate.value).toLowerCase().includes(query)) ||
      (qCanon.length > 0 &&
        (canon(cleanLabel).includes(qCanon) ||
          canon(candidate.label).includes(qCanon) ||
          (valueIsSearchableText &&
            canon(String(candidate.value)).includes(qCanon))))
    );
  };

  const resolvedCloseMenuOnSelect =
    closeMenuOnSelect ?? (isMany ? false : true);

  const select = (
    <Select
      className={className ?? "text-sm text-gray-800 !h-auto"}
      placeholder={placeholder}
      value={value as any}
      isMulti={isMany}
      onChange={handleChange as any}
      styles={styles}
      options={normalizedOptions as any}
      isLoading={isLoading}
      loadingMessage={() => loadingText ?? "Loading options..."}
      noOptionsMessage={() => noOptionsText ?? "No options found"}
      filterOption={filterOption}
      isSearchable
      {...((menuActionsEnabled
        ? {
            menuIsOpen,
            onMenuOpen: () => setMenuIsOpen(true),
            onMenuClose: () => {
              setMenuIsOpen(false);
              setInputValue("");
            },
            menuSearch: inputValue,
            onMenuSearchChange: setInputValue,
            searchInstanceId: instanceId,
            onMenuToggle: () =>
              setMenuIsOpen((open) => {
                if (open) {
                  setInputValue("");
                  return false;
                }
                return true;
              }),
          }
        : {}) as any)}
      isClearable={
        hasLabel
          ? false
          : isMany
            ? safeSelectedOptions.some((o) => !o.isFixed)
            : isClearable
      }
      isDisabled={isDisabled}
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={resolvedCloseMenuOnSelect}
      menuPlacement={menuPlacement}
      menuPortalTarget={resolvedMenuPortalTarget}
      menuPosition={resolvedMenuPortalTarget != null ? "fixed" : "absolute"}
      isOptionDisabled={resolvedIsOptionDisabled as any}
      components={customComponents}
    />
  );

  return select;
}

type Props = {
  start: { year: number; month: number };
  end: { year: number; month: number };
  selectedOptions: Option[];
  onChange: (selected: Option[]) => void;
  isMany?: boolean;
  placeholder?: string;
  customRanges?: Option[]; // Optional custom date ranges
};

export const generateMonthRanges = (
  start: { year: number; month: number },
  end: { year: number; month: number },
) => {
  const result: Option[] = [];
  let currentYear = start.year;
  let currentMonth = start.month;

  while (
    currentYear < end.year ||
    (currentYear === end.year && currentMonth <= end.month)
  ) {
    const startDate = new Date(currentYear, currentMonth - 1, 1);
    const endDate = new Date(currentYear, currentMonth, 0);

    const formattedStart = startDate.toISOString().split("T")[0];
    const formattedEnd = endDate.toISOString().split("T")[0];
    const monthLabel = startDate
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const yearLabel = String(currentYear).slice(-2);

    result.push({
      value: `${formattedStart},${formattedEnd}`,
      label: `${monthLabel}-${yearLabel}`,
    });

    if (currentMonth === 12) {
      currentMonth = 1;
      currentYear++;
    } else {
      currentMonth++;
    }
  }
  return result;
};

const SelectOption: React.FC<Props> = ({
  start,
  end,
  selectedOptions,
  onChange,
  isMany = false,
  placeholder = "Select Date Range",
  customRanges = [],
}) => {
  const options = generateMonthRanges(start, end).concat(customRanges);

  if (isMany) {
    return (
      <SearchBox
        options={options}
        selectedOptions={selectedOptions}
        onChange={onChange}
        placeholder={placeholder}
        isMany={true}
      />
    );
  }

  return (
    <SearchBox
      options={options}
      selectedOptions={selectedOptions}
      onChange={(selected) => {
        if (selected == null) {
          onChange([]);
          return;
        }
        onChange([selected]);
      }}
      placeholder={placeholder}
      isMany={false}
    />
  );
};

export default SelectOption;
